import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { ShieldCheck, GoogleLogo, ChartLineUp, Handshake, Lock, Crown, Buildings, UsersThree, Headset, Eye, Info } from "@phosphor-icons/react";
import { Eye as EyeIcon, EyeOff as EyeOffIcon, Mail } from "lucide-react";
import { OtpInput } from "@/components/OtpInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logEvent } from "@/lib/analytics";

// Fires `second_login` the 2nd time a given user authenticates (strongest activation-quality signal).
function trackReturnLogin(u) {
  try {
    const uid = u?.user_id || u?.email;
    if (!uid) return;
    const key = `ros_login_n_${uid}`;
    const n = (parseInt(localStorage.getItem(key) || "0", 10) || 0) + 1;
    localStorage.setItem(key, String(n));
    if (n === 2) logEvent("second_login", { role: u?.role || "" });
  } catch (e) { /* noop */ }
}
import { toast } from "sonner";
import { CompactTrustRow } from "@/components/PaymentTrust";
import { FloatingDemoButton } from "@/components/FloatingDemoButton";
import { goToApp } from "@/lib/domains";

const DEMO_ROLES = [
  { email: "superadmin@trustera.io", label: "Super Admin", icon: Crown },
  { email: "admin@acmenbfc.in", label: "Client Admin", icon: Buildings },
  { email: "manager@acmenbfc.in", label: "Manager", icon: UsersThree },
  { email: "suresh@acmenbfc.in", label: "Agent", icon: Headset },
  { email: "auditor@acmenbfc.in", label: "Auditor", icon: Eye },
];

const DEMO_ENABLED =
  typeof window !== "undefined" &&
  (window.location.hostname.includes("preview.emergentagent.com") ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [busy, setBusy] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [mode, setMode] = useState("otp");        // "otp" | "password"
  const [otpStep, setOtpStep] = useState("email"); // "email" | "code"
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [otpBusy, setOtpBusy] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const finishLogin = (res) => {
    localStorage.setItem("auth_token", res.data.token);
    setUser(res.data.user);
    trackReturnLogin(res.data.user);
    if (res.data.must_change_password) {
      toast.info("Please set a new password");
      navigate("/change-password");
    } else {
      navigate("/dashboard");
    }
  };

  const requestSigninOtp = async ({ resend = false } = {}) => {
    const em = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) return toast.error("Enter a valid email");
    setOtpBusy(true);
    try {
      const res = await api.post("/auth/otp/request", { email: em, purpose: "signin" });
      if (res.data?.trusted) {
        toast.success("Welcome back — trusted device recognized.");
        finishLogin(res);
        return;
      }
      setOtpStep("code");
      setCooldown(60);
      toast.success(resend ? "New code sent" : `If an account exists, we've sent a code to ${em}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code");
    } finally { setOtpBusy(false); }
  };

  const verifySigninOtp = async (submitCode) => {
    const otp = (submitCode || code).trim();
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    setOtpBusy(true);
    try {
      const res = await api.post("/auth/otp/verify", { email: email.trim().toLowerCase(), purpose: "signin", code: otp, trust_device: trustDevice });
      finishLogin(res);
    } catch (err) {
      setCode("");
      toast.error(err.response?.data?.detail || "Invalid or expired code");
    } finally { setOtpBusy(false); }
  };


  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("auth_error")) {
      const reason = p.get("reason");
      toast.error(reason
        ? `Google sign-in failed: ${decodeURIComponent(reason)}`
        : "Google sign-in failed. Please try again or use email & password.",
        { duration: 12000 });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter email and password");
    setPwBusy(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("auth_token", res.data.token);
      setUser(res.data.user);
      trackReturnLogin(res.data.user);
      if (res.data.must_change_password) {
        toast.info("Please set a new password");
        navigate("/change-password");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email or password");
    } finally { setPwBusy(false); }
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // RecoverOS-owned Google OAuth: the backend builds the Google authorize URL and handles the callback.
    window.location.href = `${API}/auth/google/login`;
  };

  const handleDemo = async (email) => {
    setBusy(email);
    try {
      const res = await api.post("/auth/demo-login", { email });
      // Store Bearer fallback so auth works even where 3rd-party cookies are blocked.
      localStorage.setItem("auth_token", res.data.token);
      setUser(res.data.user);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Demo login failed");
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0B3D91] text-white p-12">
        <div className="flex items-center gap-2">
          <Brand dark iconSize={34} />
        </div>
        <div className="space-y-6">
          <h1 className="text-4xl xl:text-5xl font-black font-heading tracking-tight leading-tight">
            The operating system for collections & recovery.
          </h1>
          <p className="text-blue-100 text-base max-w-md">
            Multi-tenant recovery workflows, settlements, compliance and reporting — for any team that collects what it's owed.
          </p>
          <div className="space-y-3 pt-4">
            {[
              { icon: ChartLineUp, t: "Real-time collections dashboards & DPD analytics" },
              { icon: Handshake, t: "Multi-stage settlement approval workflows" },
              { icon: Lock, t: "Strict tenant isolation, RBAC & immutable audit" },
            ].map((f) => (
              <div key={f.t} className="flex items-center gap-3 text-sm text-blue-50">
                <f.icon size={20} weight="bold" className="text-[#7ef0a9]" />
                {f.t}
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-blue-200">A product of Trustera Services</div>
      </div>

      {/* Right login */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <Brand iconSize={30} />
          </div>
          <div className="border border-border p-8">
            <h2 className="text-2xl font-bold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">
              {mode === "otp"
                ? "Enter your email and we'll send you a 6-digit sign-in code."
                : "Sign in with your email & password."}
            </p>

            {mode === "otp" ? (
              otpStep === "email" ? (
                <form onSubmit={(e) => { e.preventDefault(); requestSigninOtp(); }} className="space-y-3" data-testid="otp-email-form">
                  <div>
                    <Label className="text-xs">Email Address</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="rounded-sm" data-testid="login-email" />
                    <p className="mt-1 text-xs text-muted-foreground">We'll email you a one-time code — no password needed.</p>
                  </div>
                  <Button type="submit" disabled={otpBusy} className="w-full h-11 rounded-sm bg-[#16A34A] hover:bg-[#138a3e] text-white" data-testid="otp-continue-button">
                    {otpBusy ? "Sending code…" : "Continue"}
                  </Button>
                </form>
              ) : (
                <div data-testid="otp-code-step">
                  <button type="button" onClick={() => { setOtpStep("email"); setCode(""); }} className="text-xs text-[#0B3D91] hover:underline mb-3" data-testid="otp-change-email">← Change email</button>
                  <div className="flex items-center justify-center h-11 w-11 rounded-full bg-[#0B3D91]/10 mb-3"><Mail className="h-5 w-5 text-[#0B3D91]" /></div>
                  <p className="text-sm text-muted-foreground">We've sent a 6-digit verification code to:</p>
                  <p className="text-sm font-semibold mb-4" data-testid="otp-target-email">{email.trim().toLowerCase()}</p>
                  <form onSubmit={(e) => { e.preventDefault(); verifySigninOtp(); }} className="space-y-4">
                    <OtpInput value={code} onChange={setCode} onComplete={(v) => verifySigninOtp(v)} disabled={otpBusy} testId="signin-otp" />
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} className="h-4 w-4 accent-[#0B3D91]" data-testid="trust-device-checkbox" />
                      <span className="text-xs text-muted-foreground">Trust this device for 30 days (skip the code next time)</span>
                    </label>
                    <Button type="submit" disabled={otpBusy || code.length !== 6} className="w-full h-11 rounded-sm bg-[#16A34A] hover:bg-[#138a3e] text-white" data-testid="otp-verify-button">
                      {otpBusy ? "Verifying…" : "Verify & Sign In"}
                    </Button>
                  </form>
                  <div className="mt-4 text-center text-sm text-muted-foreground">
                    Didn't get the code?{" "}
                    {cooldown > 0
                      ? <span data-testid="otp-cooldown">Resend in {cooldown}s</span>
                      : <button onClick={() => requestSigninOtp({ resend: true })} disabled={otpBusy} className="font-semibold text-[#0B3D91] hover:underline" data-testid="otp-resend">Resend code</button>}
                  </div>
                </div>
              )
            ) : (
              <form onSubmit={handlePasswordLogin} className="space-y-3" data-testid="password-login-form">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="rounded-sm" data-testid="login-email" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Password</Label>
                    <button type="button" onClick={() => setForgotOpen(true)} className="text-xs text-[#0B3D91] hover:underline" data-testid="forgot-password-link">Forgot password?</button>
                  </div>
                  <div className="relative">
                    <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-sm pr-10" data-testid="login-password" />
                    <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1} aria-label={showPw ? "Hide password" : "Show password"} className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground" data-testid="login-password-toggle">
                      {showPw ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={pwBusy} className="w-full h-11 rounded-sm bg-[#16A34A] hover:bg-[#138a3e] text-white" data-testid="password-login-button">
                  {pwBusy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            )}

            <div className="mt-3 text-center">
              {mode === "otp" ? (
                <button type="button" onClick={() => { setMode("password"); setCode(""); setOtpStep("email"); }} className="text-xs text-muted-foreground hover:text-[#0B3D91]" data-testid="use-password-link">Sign in with password</button>
              ) : (
                <button type="button" onClick={() => { setMode("otp"); setPassword(""); }} className="text-xs text-muted-foreground hover:text-[#0B3D91]" data-testid="use-otp-link">Sign in with an email code instead</button>
              )}
            </div>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="bg-background px-3 label-caps">or</span></div>
            </div>

            <Button
              onClick={handleLogin}
              data-testid="google-login-button"
              variant="outline"
              className="w-full h-11 rounded-sm gap-2"
            >
              <GoogleLogo size={20} weight="bold" />
              Continue with Google
            </Button>
            <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="google-redirect-note">
              You'll be securely redirected to Google to sign in.
            </p>
            <p className="mt-2 text-center text-[11px] text-muted-foreground/80" data-testid="login-business-note">
              RecoverOS is designed for businesses — we recommend using your official company email.
            </p>

            <div className="mt-4 text-center">
              <button type="button" onClick={() => setHelpOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#0B3D91] transition-colors"
                data-testid="need-help-signing-in">
                <Info size={14} weight="bold" /> Need help signing in?
              </button>
            </div>

            {/* Demo access (preview/local only — hidden on production) */}
            {DEMO_ENABLED && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center"><span className="bg-background px-3 label-caps">Demo Access</span></div>
                </div>
                <p className="text-xs text-muted-foreground mb-3 text-center">
                  Explore any role instantly — no Google account needed.
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {DEMO_ROLES.map((r) => (
                    <button
                      key={r.email}
                      onClick={() => handleDemo(r.email)}
                      disabled={!!busy}
                      data-testid={`demo-login-${r.label.toLowerCase().replace(/ /g, "-")}`}
                      className="flex items-center gap-3 px-3 py-2.5 border border-border hover:border-[#0B3D91] hover:bg-secondary transition-colors text-left disabled:opacity-50"
                    >
                      <r.icon size={18} weight="bold" className="text-[#0B3D91] shrink-0" />
                      <span className="text-sm font-medium flex-1">{r.label}</span>
                      <span className="label-caps">{busy === r.email ? "…" : "Enter"}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <CompactTrustRow className="mt-5" />
        </div>
      </div>
      <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} />
      <SigninHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)}
        onForgot={() => { setHelpOpen(false); setForgotOpen(true); }} />
      <FloatingDemoButton onStartTrial={() => goToApp("/signup", navigate)} onBookDemo={() => navigate("/demo")} source="login-fab" />
    </div>
  );
}

function ForgotPasswordDialog({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) { setEmail(""); setBusy(false); setSent(false); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      return toast.error("Please enter a valid email address");
    }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many reset requests. Please try again later.");
      } else {
        // Backend always returns success to avoid leaking which emails exist;
        // show the same confirmation on any non-throttle error.
        setSent(true);
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>
            {sent
              ? "Check your inbox for the reset link."
              : "Enter the email you use to sign in to RecoverOS and we'll send you a secure reset link (valid 60 minutes)."}
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="space-y-4" data-testid="forgot-sent-state">
            <div className="flex items-start gap-3 border border-border bg-secondary/40 p-4 rounded-sm">
              <ShieldCheck size={22} weight="fill" className="text-[#0B3D91] shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                If an account exists for <strong className="text-foreground">{email.trim().toLowerCase()}</strong>, a password-reset link has been sent. The link expires in 60 minutes.
              </p>
            </div>
            <Button variant="outline" className="rounded-sm w-full" onClick={onClose} data-testid="forgot-done">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="rounded-sm" data-testid="forgot-email" autoFocus />
            </div>
            <Button type="submit" disabled={busy} className="rounded-sm w-full" data-testid="forgot-submit">
              {busy ? "Sending…" : "Send reset link"}
            </Button>
            <a
              href="mailto:support@recoveros.in?subject=Password%20reset%20request"
              className="block text-center text-xs text-muted-foreground hover:text-[#0B3D91] hover:underline"
              data-testid="forgot-contact-support"
            >
              Still stuck? Email support@recoveros.in
            </a>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SigninHelpDialog({ open, onClose, onForgot }) {
  const Item = ({ title, children }) => (
    <div className="border border-border rounded-sm p-3">
      <div className="text-sm font-semibold text-foreground mb-1">{title}</div>
      <div className="text-xs text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="signin-help-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info size={18} weight="bold" className="text-[#0B3D91]" /> Can't access your RecoverOS account?
          </DialogTitle>
          <DialogDescription>A few quick answers to the most common sign-in questions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Item title="Forgot your password?">
            Click{" "}
            <button type="button" onClick={onForgot} className="text-[#0B3D91] font-semibold hover:underline" data-testid="help-forgot-link">Forgot password?</button>
            , enter your work email, and we'll send you a password reset link that's valid for <strong className="text-foreground">60 minutes</strong>.
          </Item>
          <Item title="I signed up with Google">
            If you registered using <strong className="text-foreground">Continue with Google</strong>, simply sign in again using Google — you don't need to reset a password.
          </Item>
          <Item title="Didn't receive the email?">
            Check your <strong className="text-foreground">Spam</strong> or <strong className="text-foreground">Promotions</strong> folder. The email is sent from{" "}
            <a href="mailto:support@recoveros.in" className="text-[#0B3D91] hover:underline">support@recoveros.in</a>. If you still can't find it, contact your organization administrator or RecoverOS Support.
          </Item>
          <Item title="First login with a temporary password?">
            You'll be asked to create a new password after signing in for the first time.
          </Item>
        </div>
        <Button variant="outline" className="rounded-sm w-full mt-1" onClick={onClose} data-testid="help-close">Got it</Button>
      </DialogContent>
    </Dialog>
  );
}
